import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { allAPiServicesCall } from "../../services/apiServices";
import { getAuthConfig } from "../../services/apiUtils";
import { toast } from "react-toastify";
import { extractObjectId } from "../../utils/utils";

function SetPlaylistPriorityModel({ show, handleClose, playlistDetails, handleFormSubmit }) {
  const inTop = !!playlistDetails?.isTopPlaylist;
  const inExplore = !!playlistDetails?.isExplorePlaylist;

  const defaultPlacement = useMemo(() => {
    if (inTop) return "top";
    if (inExplore) return "explore";
    return "top";
  }, [inTop, inExplore]);

  const [placementType, setPlacementType] = useState(defaultPlacement);
  const [priority, setPriority] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset state whenever a different playlist is opened in the modal.
  useEffect(() => {
    if (!playlistDetails) return;
    setPlacementType(defaultPlacement);
    const seed =
      defaultPlacement === "top"
        ? playlistDetails?.topPlacementPriority
        : playlistDetails?.explorePlacementPriority;
    setPriority(seed != null ? String(seed) : "");
  }, [playlistDetails, defaultPlacement]);

  // When the admin flips the radio, swap the priority field to that placement's
  // current value so they don't accidentally overwrite Top with Explore's number.
  const handlePlacementChange = (next) => {
    setPlacementType(next);
    const seed =
      next === "top"
        ? playlistDetails?.topPlacementPriority
        : playlistDetails?.explorePlacementPriority;
    setPriority(seed != null ? String(seed) : "");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!playlistDetails) return;

    const numericPriority = Number(priority);
    if (!Number.isFinite(numericPriority) || numericPriority < 1) {
      toast.error("Priority must be a positive number");
      return;
    }
    if (placementType === "top" && !inTop) {
      toast.error("This playlist is not in Top for the selected page target");
      return;
    }
    if (placementType === "explore" && !inExplore) {
      toast.error("This playlist is not in Explore for the selected page target");
      return;
    }

    const payload = {
      playlist_id: extractObjectId(playlistDetails._id),
      placementType,
      priority: numericPriority,
      pageType: playlistDetails?.pageType,
      genreSlug: playlistDetails?.genreSlug,
    };

    try {
      setSubmitting(true);
      const response = await allAPiServicesCall.patchUpdatePlaylistPriority(
        payload,
        getAuthConfig()
      );
      if (response && response.success) {
        toast.success(response.message || "Playlist priority updated");
        handleFormSubmit?.();
        handleClose();
      } else {
        toast.error(response?.message || "Failed to update priority");
      }
    } catch (error) {
      console.error("Failed to update playlist priority:", error?.response?.data || error);
      toast.error("An error occurred while updating priority");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered fullscreen="sm-down">
      <Modal.Header closeButton>
        <Modal.Title>Edit Playlist Priority</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={onSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Placement</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id="placement-top"
                name="placementType"
                label="Top"
                checked={placementType === "top"}
                disabled={!inTop}
                onChange={() => handlePlacementChange("top")}
              />
              <Form.Check
                type="radio"
                id="placement-explore"
                name="placementType"
                label="Explore"
                checked={placementType === "explore"}
                disabled={!inExplore}
                onChange={() => handlePlacementChange("explore")}
              />
            </div>
            {!inTop && !inExplore && (
              <Form.Text className="text-danger">
                This playlist isn't currently in Top or Explore for the selected
                page target. Toggle it on first, then set a priority.
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Set Priority (1 = top of list)</Form.Label>
            <Form.Control
              type="number"
              min="1"
              name="priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              required
              style={{ minHeight: "44px" }}
            />
          </Form.Group>

          <div className="d-flex gap-2 mt-4">
            <Button
              variant="secondary"
              onClick={handleClose}
              className="flex-grow-1"
              style={{ minHeight: "44px" }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              className="flex-grow-1"
              style={{ minHeight: "44px" }}
              disabled={submitting || (!inTop && !inExplore)}
            >
              {submitting ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default SetPlaylistPriorityModel;
